<script setup>
import { ref, computed, watch, reactive } from 'vue'
import { storeToRefs } from 'pinia'
import { useUsersStore, useRestaurantsStore, useReservationsStore } from '@/stores'
import RestaurantGridCard from "@core/components/home/content/RestaurantGridCard.vue"
import CmsText from '@/@core/components/CmsText.vue'

const props = defineProps({
  isDialogVisible: {
    type: Boolean,
    default: false
  },

  // Tab to land on when the dialog opens (e.g. 'preferred-benefits' from
  // the Preferred Restaurants link under the discovery tabs)
  initialTab: {
    type: String,
    default: 'how-rm-works'
  }
})

const emit = defineEmits(['update:isDialogVisible'])

const usersStore = useUsersStore()
const restaurantsStore = useRestaurantsStore()
const reservationsStore = useReservationsStore()

const myUser = computed(() => usersStore.myUser)
const { loginDialogMode, loginDialogVerificationType } = storeToRefs(usersStore)
const { preferredRestaurants, citySelectorData } = storeToRefs(restaurantsStore)
const { editingReservation } = storeToRefs(reservationsStore)

const tab = ref('how-rm-works')

// City filter state
const selectedCity = ref('')

// City filter bottom sheet state
const isCityFilterOpen = ref(false)
const citySearchQuery = ref('')
const pendingCitySelection = ref('')

// City selector options from backend
const selectableCities = computed(() => {
  return citySelectorData.value?.cities || []
})

// Filtered cities for bottom sheet search
const filteredCityOptions = computed(() => {
  const cities = selectableCities.value
  if (!citySearchQuery.value) return cities
  const query = citySearchQuery.value.toLowerCase()

  return cities.filter(c => c.text.toLowerCase().includes(query))
})

// Open city filter bottom sheet
const openCityFilter = () => {
  pendingCitySelection.value = selectedCity.value
  citySearchQuery.value = ''
  isCityFilterOpen.value = true
  handleCitySelectorFocus()
}

// Apply city filter selection from bottom sheet
const applyCityFilter = () => {
  selectedCity.value = pendingCitySelection.value
  isCityFilterOpen.value = false
  handleCityChange()
}

// Pagination and loading state
const isLoading = ref(false)
const isLoadingMore = ref(false)
const hasMoreData = ref(true)
const isPrefetching = ref(false)
const hasPrefetchedNext = ref(false)

const pagination = reactive({
  limit: 20,
  skip: 0,
  prefetchedData: null
})

// Get filtered/sorted preferred restaurants (always alphabetical)
const displayedRestaurants = computed(() => {
  if (!preferredRestaurants.value) return []

  let filtered = preferredRestaurants.value.filter(r => r.status === 'Preferred')

  // Apply city filter
  if (selectedCity.value) {
    filtered = filtered.filter(r => r.market === selectedCity.value)
  }

  // Always sort alphabetically
  filtered = [...filtered].sort((a, b) => {
    const nameA = (a.name || '').toLowerCase()
    const nameB = (b.name || '').toLowerCase()

    return nameA.localeCompare(nameB)
  })

  return filtered
})

// Load initial preferred restaurants data
const loadPreferredRestaurants = async (city = null, reset = true) => {
  isLoading.value = true
  try {
    if (reset) {
      pagination.skip = 0
      pagination.prefetchedData = null
      hasMoreData.value = true
      hasPrefetchedNext.value = false
    }

    await restaurantsStore.getPreferredRestaurants(pagination.limit, city, pagination.skip, false)
  } finally {
    isLoading.value = false
  }
}

// Prefetch next page of restaurants in background
const prefetchNextPage = async () => {
  if (editingReservation?.value) return
  if (isPrefetching.value || !hasMoreData.value || hasPrefetchedNext.value) return

  isPrefetching.value = true
  try {
    const nextSkip = pagination.skip + pagination.limit

    const prefetchedData = await restaurantsStore.getPreferredRestaurants(
      pagination.limit,
      selectedCity.value || null,
      nextSkip,
      false,
      true
    )

    pagination.prefetchedData = prefetchedData
    hasPrefetchedNext.value = true

    if (!prefetchedData || prefetchedData.length < pagination.limit) {
      hasMoreData.value = false
    }
  } catch (error) {
    console.error('Error prefetching:', error)
  } finally {
    isPrefetching.value = false
  }
}

// Load more restaurants for infinite scrolling
const loadMoreRestaurants = async () => {
  if (editingReservation?.value) return
  if (isLoadingMore.value || !hasMoreData.value) return

  isLoadingMore.value = true
  try {
    if (pagination.prefetchedData) {
      const cachedData = pagination.prefetchedData
      restaurantsStore.preferredRestaurants = [...restaurantsStore.preferredRestaurants, ...cachedData]

      pagination.skip += pagination.limit
      pagination.prefetchedData = null
      hasPrefetchedNext.value = false

      setTimeout(() => prefetchNextPage(), 100)
    } else {
      pagination.skip += pagination.limit

      const newData = await restaurantsStore.getPreferredRestaurants(
        pagination.limit,
        selectedCity.value || null,
        pagination.skip,
        true
      )

      if (!newData || newData.length < pagination.limit) {
        hasMoreData.value = false
      }

      setTimeout(() => prefetchNextPage(), 100)
    }
  } finally {
    isLoadingMore.value = false
  }
}

// Handle city selection change
const handleCityChange = async () => {
  hasMoreData.value = true
  hasPrefetchedNext.value = false
  await loadPreferredRestaurants(selectedCity.value || null, true)
}

// Load city selector data on focus (lazy load)
const handleCitySelectorFocus = async () => {
  if (!citySelectorData.value || !citySelectorData.value.cities) {
    try {
      await restaurantsStore.getCitySelectorData()
    } catch (error) {
      console.error('Error loading city selector data:', error)
    }
  }
}

// Handle scroll event for infinite scrolling within dialog
const handleDialogScroll = event => {
  const target = event.target
  const scrollTop = target.scrollTop
  const scrollHeight = target.scrollHeight
  const clientHeight = target.clientHeight

  const scrollPercentage = (scrollTop + clientHeight) / scrollHeight

  // Start prefetching when user reaches 60% of content
  if (scrollPercentage > 0.6 && !hasPrefetchedNext.value && !isPrefetching.value && hasMoreData.value) {
    prefetchNextPage()
  }

  // Load more when user is 400px from bottom
  if (scrollTop + clientHeight >= scrollHeight - 400) {
    loadMoreRestaurants()
  }
}

// Load data when dialog opens
watch(() => props.isDialogVisible, async newVal => {
  if (newVal) {
    tab.value = props.initialTab
    const hasData = preferredRestaurants.value && preferredRestaurants.value.length > 0
    if (!hasData) {
      await loadPreferredRestaurants()
    }
    if (!citySelectorData.value || !citySelectorData.value.cities) {
      restaurantsStore.getCitySelectorData()
    }
  }
}, { immediate: true })

const closeDialog = () => {
  emit('update:isDialogVisible', false)
}
</script>

<template>
  <div>
    <VDialog
      :model-value="isDialogVisible"
      max-width="500"
      scrollable
      @update:model-value="$emit('update:isDialogVisible', $event)"
    >
      <VCard>
        <VCardTitle class="pa-4 pb-2">
          <VBtn
            icon
            variant="text"
            size="small"
            color="black"
            class="close-btn"
            @click="closeDialog"
          >
            <VIcon>mdi-close</VIcon>
          </VBtn>
          <div class="title-container text-center mt-4">
            <span class="title-text">Preferred Restaurants:</span>
            <CmsText
              k="restaurants.masthead"
              tag="span"
              class="subtitle-text"
            />
          </div>
        </VCardTitle>
        <div class="px-4 text-center">
          <VBtn
            variant="flat"
            size="small"
            color="primary"
            @click="loginDialogVerificationType = 'restaurant'; loginDialogMode = 'activate';"
          >
            <span>Request Free Restaurant Account</span>
          </VBtn>
        </div>
        <div class="px-4">
          <VTabs
            v-model="tab"
            class="full-width-tabs"
            grow
            show-arrows="false"
          >
            <VTab
              value="how-rm-works"
              class="text-xs"
            >
              How RM Works
            </VTab>
            <VTab
              value="preferred-benefits"
              class="text-xs"
            >
              Benefits
            </VTab>
            <VTab
              value="faqs"
              class="text-xs"
            >
              FAQs
            </VTab>
          </VTabs>
        </div>

        <VDivider class="mx-4" />

        <VCardText
          class="pa-4 dialog-scroll-container"
          @scroll="handleDialogScroll"
        >
          <VWindow v-model="tab">
            <VWindowItem
              class="pt-4 text-xs"
              value="how-rm-works"
            >
              <div class="tab-content-width">
                <CmsText
                  k="restaurants.tabs.howRmWorks"
                  tag="div"
                  class="section-subtitle text-left mt-2 tab-content"
                  style="line-height: 1.4em;"
                />
              </div>
            </VWindowItem>

            <VWindowItem
              class="pt-4 text-xs"
              value="preferred-benefits"
            >
              <div class="tab-content-width">
                <CmsText
                  k="restaurants.tabs.preferredBenefits"
                  tag="div"
                  class="section-subtitle text-left mt-2 tab-content"
                  style="line-height: 1.4em;"
                />
              </div>
            </VWindowItem>

            <VWindowItem
              class="pt-4 text-xs"
              value="faqs"
            >
              <div class="tab-content-width">
                <CmsText
                  k="restaurants.tabs.faqs"
                  tag="div"
                  class="section-subtitle text-left mt-2 tab-content"
                  style="line-height: 1.4em;"
                />
              </div>
            </VWindowItem>
          </VWindow>

          <!-- Preferred Restaurants List -->
          <div class="preferred-restaurants-section mt-6">
            <h3 class="section-header text-center mb-4">
              Preferred Restaurants
            </h3>

            <!-- City Filter Trigger -->
            <div class="city-filter-wrapper mb-4">
              <div
                class="city-filter-trigger"
                @click="openCityFilter"
              >
                <VIcon
                  icon="mdi-map-marker"
                  size="18"
                  color="primary-dark"
                />
                <span class="city-filter-trigger-text">{{ selectableCities.find(c => c.value === selectedCity)?.text || 'All Cities' }}</span>
                <VIcon
                  icon="mdi-chevron-down"
                  size="18"
                />
              </div>
            </div>

            <!-- Restaurant Grid -->
            <div
              v-if="displayedRestaurants.length > 0"
              class="restaurant-list-wrapper"
            >
              <div class="restaurant-list">
                <RestaurantGridCard
                  v-for="(item, index) in displayedRestaurants"
                  :key="item._id || item.raw?._id"
                  :item="item"
                  :index="index"
                  @open-choose-member-dialog="(market, restaurant) => usersStore.startNewReservation({ restaurant, market })"
                />
              </div>
            </div>

            <!-- Loading State -->
            <div
              v-else-if="isLoading"
              class="loading-state"
            >
              <VProgressCircular
                indeterminate
                color="primary"
                size="64"
              />
            </div>

            <!-- Empty State -->
            <div
              v-else
              class="empty-state"
            >
              <p class="text-center text-grey">
                No preferred restaurants found{{ selectedCity ? ' for this city' : '' }}.
              </p>
            </div>

            <!-- Prefetching Indicator -->
            <div
              v-if="isPrefetching && displayedRestaurants.length > 0 && !isLoading"
              class="prefetching-indicator"
            >
              <VProgressCircular
                indeterminate
                color="primary"
                size="24"
              />
              <p class="prefetching-text">
                Loading more...
              </p>
            </div>
          </div>
        </VCardText>
      </VCard>
    </VDialog>

    <!-- City Filter Bottom Sheet -->
    <VBottomSheet v-model="isCityFilterOpen">
      <VCard class="city-filter-sheet">
        <div class="sheet-handle" />
        <div class="sheet-header">
          <span class="sheet-title">Filter by City</span>
          <VBtn
            icon
            variant="text"
            size="small"
            @click="isCityFilterOpen = false"
          >
            <VIcon size="20">
              mdi-close
            </VIcon>
          </VBtn>
        </div>
        <VDivider />

        <!-- Search -->
        <div class="city-search-wrapper">
          <VTextField
            v-model="citySearchQuery"
            placeholder="Search cities..."
            variant="outlined"
            density="compact"
            hide-details
            prepend-inner-icon="mdi-magnify"
            class="city-search-input"
          />
        </div>

        <!-- City list -->
        <div class="city-list">
          <div
            v-for="city in filteredCityOptions"
            :key="city.value"
            class="city-list-item"
            @click="pendingCitySelection = city.value"
          >
            <span class="city-list-item-text">{{ city.text }}</span>
            <span
              class="city-radio"
              :class="{ 'city-radio--selected': pendingCitySelection === city.value }"
            />
          </div>
        </div>

        <!-- Done button -->
        <div class="city-done-wrapper">
          <VBtn
            block
            color="primary-dark"
            rounded="lg"
            size="large"
            @click="applyCityFilter"
          >
            Done
          </VBtn>
        </div>
      </VCard>
    </VBottomSheet>
  </div>
</template>

<style lang="scss" scoped>
.close-btn {
  position: absolute;
  top: var(--ds-space-2);
  right: var(--ds-space-2);
}

// VCardTitle defaults to `white-space: nowrap; overflow: hidden;
// text-overflow: ellipsis`, which the title spans inherit — truncating the
// multi-line masthead subtitle. Allow the title to wrap normally.
:deep(.v-card-title) {
  white-space: normal;
}

.title-container {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}

.title-text {
  font-size: 0.9rem;
  font-weight: 900;
  margin: 0;
  color: var(--ds-text-strong);
  white-space: normal;
}

.subtitle-text {
  font-size: 0.85rem;
  font-weight: 400;
  margin: 0;
  color: var(--ds-text-strong);
  white-space: normal;
  overflow-wrap: anywhere;
}

.tab-content-width {
  font-size: 0.8em;
}

.full-width-tabs {
  width: 100%;
}

.full-width-tabs .v-tab {
  flex: 1;
  max-width: 33.333%;
  justify-content: center;
  min-width: 0;
  padding: 0 var(--ds-space-2);
  font-size: 0.75rem;
  white-space: normal;
  text-align: center;
  line-height: var(--ds-leading-tight);
}

@media (max-width: 600px) {
  .full-width-tabs .v-tab {
    padding: 0 var(--ds-space-1);
    font-size: 0.7rem;
  }
}

.full-width-tabs .v-tab .v-btn__content {
  color: var(--ds-text-strong) !important;
  white-space: normal;
}

.tab-content {
  font-family: inherit !important;
  color: rgba(0, 0, 0, 0.6) !important;
}

.tab-content :deep(p) {
  margin: 0 !important;
  font-family: inherit !important;
  font-size: 0.75rem !important;
  line-height: 16px !important;
  color: rgba(0, 0, 0, 0.6) !important;
}

.tab-content :deep(b) {
  font-family: inherit !important;
  color: rgba(0, 0, 0, 0.6) !important;
}

.tab-content :deep(a) {
  color: rgb(var(--v-theme-primary));
}

.tab-content :deep(p.section-title) {
  margin-top: 1rem !important;
}

.tab-content :deep(p.section-title:first-child) {
  margin-top: 0 !important;
}

.dialog-scroll-container {
  max-height: 70vh;
  overflow-y: auto;
}

.preferred-restaurants-section {
  width: 100%;
}

.section-header {
  font-family: var(--ds-font-body);
  font-size: var(--ds-text-lg);
  font-weight: 600;
  color: var(--ds-brand-primary-dark);
  margin-bottom: var(--ds-space-4);
}

.restaurant-list-wrapper {
  width: 100%;
}

.restaurant-list {
  display: flex;
  flex-direction: column;
  gap: var(--ds-space-3);
  width: 100%;
}

.loading-state {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px 20px;
  min-height: 200px;
}

.empty-state {
  padding: 40px 20px;
  text-align: center;
  color: var(--ds-text-muted);
}

.prefetching-indicator {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--ds-space-2);
  padding: var(--ds-space-6) 0;
}

.prefetching-text {
  font-family: var(--ds-font-body);
  font-size: var(--ds-text-base);
  color: var(--ds-text-muted);
  margin: 0;
}

// City filter trigger button
.city-filter-wrapper {
  display: flex;
  justify-content: center;
}

.city-filter-trigger {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border-radius: var(--ds-radius-md);
  background: var(--ds-surface-subtle);
  padding: var(--ds-space-2) var(--ds-space-4);
  cursor: pointer;
  transition: background var(--ds-duration-normal) ease;
  width: fit-content;

  &:hover {
    background: #ebebeb;
  }
}

.city-filter-trigger-text {
  font-family: var(--ds-font-body);
  font-size: var(--ds-text-md);
  font-weight: 500;
  color: var(--ds-brand-primary-dark);
}

// City filter bottom sheet
.city-filter-sheet {
  border-radius: var(--ds-radius-lg) var(--ds-radius-lg) 0 0 !important;
  min-block-size: 70vh;
  max-block-size: 70vh;
  display: flex;
  flex-direction: column;
}

.sheet-handle {
  width: 36px;
  height: 4px;
  border-radius: var(--ds-radius-xs);
  background: rgba(var(--v-theme-on-surface), 0.2);
  margin: 10px auto 4px;
}

.sheet-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px 8px 20px;
}

.sheet-title {
  font-size: var(--ds-text-md);
  font-weight: 600;
  color: rgba(var(--v-theme-on-surface), 0.87);
}

.city-search-wrapper {
  padding: 12px 16px 8px;
}

.city-search-input {
  :deep(.v-field) {
    border-radius: var(--ds-radius-sm);
    font-size: 16px; // prevents iOS zoom
  }
}

.city-list {
  overflow-y: auto;
  flex: 1;
  padding: 0 4px;
}

.city-list-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  cursor: pointer;
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.06);

  &:last-child {
    border-bottom: none;
  }

  &:active {
    background: rgba(var(--v-theme-on-surface), 0.04);
  }
}

.city-list-item-text {
  font-family: var(--ds-font-body);
  font-size: var(--ds-text-md);
  font-weight: 400;
  color: rgba(var(--v-theme-on-surface), 0.87);
}

.city-radio {
  width: 22px;
  height: 22px;
  border-radius: var(--ds-radius-full);
  border: 2px solid rgba(var(--v-theme-on-surface), 0.3);
  flex-shrink: 0;
  transition: all var(--ds-duration-fast) ease;

  &--selected {
    border-color: var(--ds-brand-primary-dark);
    background: var(--ds-brand-primary-dark);
    box-shadow: inset 0 0 0 3px #fff;
  }
}

.city-done-wrapper {
  padding: 12px 16px;
  border-top: 1px solid rgba(var(--v-theme-on-surface), 0.08);
}
</style>
